import React, { useState } from 'react';
import { Phone, X, Loader2, Check, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useI18n } from '../i18n/I18nProvider';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FloatingContact() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', telephone: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.telephone) {
      toast.error(t('floating.toastRequired'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/leads/callback`, form);
      setDone(true);
      toast.success(t('floating.toastSuccess'));
    } catch {
      toast.error(t('floating.toastError'));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setOpen(false);
    setTimeout(() => { setDone(false); setForm({ name: '', telephone: '' }); }, 300);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-[70] h-14 w-14 rounded-full bg-[#76B828] text-white shadow-lg shadow-[#76B828]/30 hover:shadow-xl hover:shadow-[#76B828]/40 hover:scale-105 active:scale-95 transition-all flex items-center justify-center"
        data-testid="floating-contact-btn"
        aria-label={t('floating.aria')}
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      <div className={`fixed bottom-24 right-6 z-[70] w-[320px] transition-all duration-300 ${open ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-4 pointer-events-none'}`}>
        <div className="bg-white rounded-2xl shadow-2xl border border-black/5 overflow-hidden">
          <div className="bg-[#76B828] px-5 py-4">
            <div className="flex items-center gap-2 text-white">
              <Phone size={18} />
              <span className="font-semibold text-[15px]">{t('floating.title')}</span>
            </div>
            <p className="text-white/75 text-[13px] mt-1">{t('floating.subtitle')}</p>
          </div>

          <div className="p-5">
            {!done ? (
              <form onSubmit={submit} className="space-y-3" data-testid="callback-form">
                <div>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full rounded-xl border border-black/10 px-4 py-2.5 text-[14px] outline-none focus:border-[#76B828] focus:ring-2 focus:ring-[#76B828]/20 transition"
                    placeholder={t('floating.namePlaceholder')}
                    data-testid="callback-name"
                  />
                </div>
                <div>
                  <input
                    value={form.telephone}
                    onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                    className="w-full rounded-xl border border-black/10 px-4 py-2.5 text-[14px] outline-none focus:border-[#76B828] focus:ring-2 focus:ring-[#76B828]/20 transition"
                    placeholder="+41 00 000 00 00"
                    data-testid="callback-telephone"
                  />
                </div>
                <button type="submit" disabled={loading} className="btn-pill btn-dark w-full py-2.5 text-[14px] disabled:opacity-70" data-testid="callback-submit">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : t('floating.submit')}
                </button>
              </form>
            ) : (
              <div className="text-center py-4" data-testid="callback-success">
                <div className="mx-auto h-10 w-10 rounded-full bg-[#e9f9f0] flex items-center justify-center">
                  <Check size={20} className="text-[#16a34a]" />
                </div>
                <p className="font-semibold text-sm mt-3 text-[#0b0b0f]">{t('floating.successTitle')}</p>
                <p className="text-[#6b6b73] text-xs mt-1">{t('floating.successBody')}</p>
                <button type="button" onClick={reset} className="text-sm font-medium text-[#76B828] mt-3 hover:underline">{t('floating.close')}</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
